<template>
  <div class="title-panel">
    <div class="title-container">
      <h1>{{ $t('workspaces.workspaces') }}</h1>
    </div>

    <vf-button
      v-if="isUserRole([Role.Admin])"
      class="create-workspace-button"
      :variant="ButtonVariant.SECONDARY"
      :size="ButtonSize.LARGE"
      @click="goToWorkspaceCreation()"
      >{{ $t('workspaces.createWorkspace') }}
    </vf-button>
  </div>

  <table-wrapper
    v-model:page-options="pageOptions"
    v-model:filters="filters"
    :fields="tableFields"
    :filter-configuration="filterConfiguration"
    :items="items"
    :loading="isLoading"
    :mode="TableMode.LAZY"
    row-clickable
    @row-click="goToWorkspaceDetails"
  ></table-wrapper>
</template>

<script lang="ts" setup>
  import {
    TableField,
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '@/components/TableWrapper';
  import {
    ButtonSize,
    ButtonVariant,
  } from '@/components/VfButton/VfButton.types';
  import { useNavigator } from '@/composables/useNavigator';
  import { useRoles } from '@/composables/useRoles';
  import { useToast } from '@/composables/useToast';
  import { endpointsList } from '@/helpers';
  import { Route } from '@/routes';
  import axios from 'axios';
  import {
    IWorkspacesResponseDto,
    IWorkspaceSummaryDto,
    Role,
    WorkspaceStatus,
  } from 'types';
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';
  import { WorkspaceDetailsTab } from '../WorkspaceDetails/WorkspaceDetails.types';

  const router = useRouter();
  const { goToWorkspaceDetailsTab } = useNavigator();
  const { createErrorToast } = useToast();
  const { t } = useI18n();
  const { isUserRole } = useRoles();

  const { filters, filterParams, pageOptions, isLoading } =
    useTableWrapper<IWorkspaceSummaryDto>(
      TableMode.LAZY,
      'name',
      getWorkspaces,
    );

  const items = ref<IWorkspaceSummaryDto[]>([]);

  async function getWorkspaces(): Promise<void> {
    try {
      const response = await axios.get<IWorkspacesResponseDto>(
        endpointsList.workspaces.getWorkspaces(),
        {
          params: {
            order: pageOptions.value.sortOrder,
            page: pageOptions.value.page,
            sortBy: pageOptions.value.sortBy,
            take: pageOptions.value.perPage,
            ...filterParams.value,
          },
        },
      );
      items.value = response.data.data;
      pageOptions.value.rows = response.data.meta.itemCount;
    } catch (error) {
      createErrorToast(t('workspaces.errors.errorGettingWorkspaces'));
      throw error;
    }
  }

  const tableFields = computed<TableField<IWorkspaceSummaryDto>[]>(() =>
    new TableFieldsBuilder<IWorkspaceSummaryDto>({
      sortable: true,
      labelTranslationKey: 'workspaces',
    })
      .addField('name', {
        width: '70%',
      })
      .addField('status', {
        width: '30%',
        valueTranslationKey: 'workspaces.statuses',
      })
      .build(),
  );

  const filterConfiguration = new TableFiltersBuilder<IWorkspaceSummaryDto>({
    translationKey: 'workspaces',
  })
    .addInputFilter('name', {
      width: '280px',
    })
    .addSelectFilter('status', {
      width: '208px',
      selectOptions: Object.values(WorkspaceStatus),
      translationKey: 'workspaces.statuses',
    })
    .build();

  function goToWorkspaceCreation(): void {
    router.push({ name: Route.CREATE_WORKSPACE });
  }

  function goToWorkspaceDetails(workspace: IWorkspaceSummaryDto): void {
    goToWorkspaceDetailsTab(workspace._id, WorkspaceDetailsTab.SETTINGS);
  }
</script>

<style scoped lang="scss">
  .title-panel {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 50px;

    .title-container {
      h1 {
        font-size: 50px;
        margin: 0;
      }
    }
  }
</style>
